import { z } from "zod";
import { billingRailSchema, currencyCodeSchema, uppercaseUlidSchema } from "./billing";
const slugSchema = z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(100);
const nameSchema = z.string().trim().min(1).max(200);
const reasonSchema = z.string().trim().min(1).max(1_000);
const countryCodeSchema = z.union([z.literal("*"), z.string().regex(/^[A-Z]{2}$/)]);
const amountSchema = z.number().int().min(1).max(Number.MAX_SAFE_INTEGER);
const timestampSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
export const externalPriceIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(512)
    .regex(/^[\x21-\x7e]+$/)
    .nullable();
export const polarUuid4Schema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
export const externalProductIdSchema = polarUuid4Schema.nullable();
const statusSchema = z.enum(["draft", "active", "archived"]);
export const billingMarketSchema = z.enum(["id", "gl"]);
export const accessPassCatalogSnapshotSchema = z.strictObject({
    kind: z.literal("access_pass"),
    id: uppercaseUlidSchema,
    planId: z.string().min(1).max(100),
    planSlug: slugSchema,
    planName: nameSchema,
    monthlyInteractionQuota: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    termMonths: z.union([z.literal(1), z.literal(6), z.literal(12)]),
    amount: amountSchema,
    discountPercent: z.number().int().min(0).max(100),
    externalProductId: externalProductIdSchema,
    externalPriceId: externalPriceIdSchema,
});
export const creditPackCatalogSnapshotSchema = z.strictObject({
    kind: z.literal("credit_pack"),
    id: uppercaseUlidSchema,
    offerKey: slugSchema,
    name: nameSchema,
    interactions: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    amount: amountSchema,
    expiresDays: z.number().int().min(1).max(3_650),
    sortOrder: z.number().int().min(0).max(1_000_000),
    externalProductId: externalProductIdSchema,
    externalPriceId: externalPriceIdSchema,
});
export const accessPassOfferSchema = z
    .strictObject({
    id: uppercaseUlidSchema,
    planName: nameSchema,
    monthlyInteractionQuota: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    termMonths: z.union([z.literal(1), z.literal(6), z.literal(12)]),
    amount: amountSchema,
    currency: currencyCodeSchema,
    discountPercent: z.number().int().min(0).max(100),
    expiry: z.strictObject({
        type: z.literal("fixed_term"),
        termMonths: z.union([z.literal(1), z.literal(6), z.literal(12)]),
    }),
    prepaid: z.literal(true),
    autoRenews: z.literal(false),
    monthlyQuotaResets: z.literal(true),
})
    .refine((value) => value.expiry.termMonths === value.termMonths, {
    error: "Expiry must match the access-pass term",
});
export const creditPackOfferSchema = z
    .strictObject({
    id: uppercaseUlidSchema,
    offerKey: slugSchema,
    name: nameSchema,
    interactions: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    amount: amountSchema,
    currency: currencyCodeSchema,
    expiresDays: z.number().int().min(1).max(3_650),
    prepaid: z.literal(true),
    autoRenews: z.literal(false),
    expiry: z.strictObject({
        type: z.literal("days_after_purchase"),
        days: z.number().int().min(1).max(3_650),
    }),
})
    .refine((value) => value.expiry.days === value.expiresDays, {
    error: "Expiry disclosure must match the credit expiry",
});
export const accessPassOffersResponseSchema = z.strictObject({
    items: z.array(accessPassOfferSchema).max(50),
});
export const creditPackOffersResponseSchema = z.strictObject({
    items: z.array(creditPackOfferSchema).max(50),
});
export const opaqueCatalogCursorSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .min(1)
    .max(256);
export const adminCatalogListQuerySchema = z.strictObject({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: opaqueCatalogCursorSchema.optional(),
});
export const adminCatalogPreviewQuerySchema = z.strictObject({
    market: billingMarketSchema,
    country: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .optional(),
});
export const adminPlanSchema = z.strictObject({
    id: z.string().min(1).max(100),
    slug: slugSchema,
    name: nameSchema,
    monthlyInteractionQuota: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    status: statusSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    archivedAt: timestampSchema.nullable(),
});
export const adminPlanPriceSchema = z
    .strictObject({
    id: uppercaseUlidSchema,
    planId: z.string().min(1).max(100),
    countryCode: countryCodeSchema,
    currency: currencyCodeSchema,
    termMonths: z.union([z.literal(1), z.literal(6), z.literal(12)]),
    amount: amountSchema,
    discountPercent: z.number().int().min(0).max(100),
    rail: billingRailSchema,
    externalProductId: externalProductIdSchema,
    externalPriceId: externalPriceIdSchema,
    status: statusSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    archivedAt: timestampSchema.nullable(),
})
    .superRefine(addProviderIdentityIssues);
export const adminCreditPackSchema = z
    .strictObject({
    id: uppercaseUlidSchema,
    offerKey: slugSchema,
    slug: slugSchema,
    name: nameSchema,
    interactions: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    countryCode: countryCodeSchema,
    currency: currencyCodeSchema,
    amount: amountSchema,
    rail: billingRailSchema,
    externalProductId: externalProductIdSchema,
    externalPriceId: externalPriceIdSchema,
    expiresDays: z.number().int().min(1).max(3_650),
    sortOrder: z.number().int().min(0).max(1_000_000),
    status: statusSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    archivedAt: timestampSchema.nullable(),
})
    .superRefine(addProviderIdentityIssues);
export const adminPlanListResponseSchema = z.strictObject({
    items: z.array(adminPlanSchema).max(100),
    nextCursor: opaqueCatalogCursorSchema.nullable(),
});
export const adminPlanPriceListResponseSchema = z.strictObject({
    items: z.array(adminPlanPriceSchema).max(100),
    nextCursor: opaqueCatalogCursorSchema.nullable(),
});
export const adminCreditPackListResponseSchema = z.strictObject({
    items: z.array(adminCreditPackSchema).max(100),
    nextCursor: opaqueCatalogCursorSchema.nullable(),
});
export const adminCatalogPreviewResponseSchema = z.strictObject({
    market: billingMarketSchema,
    country: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
    accessPasses: z.array(accessPassOfferSchema).max(50),
    creditPacks: z.array(creditPackOfferSchema).max(50),
});
export const createAdminPlanInputSchema = z.strictObject({
    slug: slugSchema,
    name: nameSchema,
    monthlyInteractionQuota: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    reason: reasonSchema,
});
export const patchAdminPlanInputSchema = z
    .strictObject({
    expectedUpdatedAt: timestampSchema,
    name: nameSchema.optional(),
    monthlyInteractionQuota: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
    reason: reasonSchema,
})
    .refine((value) => value.name !== undefined || value.monthlyInteractionQuota !== undefined, {
    error: "At least one editable field is required",
});
export const createAdminPlanPriceInputSchema = z
    .strictObject({
    planId: z.string().min(1).max(100),
    countryCode: countryCodeSchema,
    currency: currencyCodeSchema,
    termMonths: z.union([z.literal(1), z.literal(6), z.literal(12)]),
    amount: amountSchema,
    discountPercent: z.number().int().min(0).max(100).default(0),
    rail: billingRailSchema,
    externalProductId: externalProductIdSchema.default(null),
    externalPriceId: externalPriceIdSchema.default(null),
    reason: reasonSchema,
})
    .superRefine(addProviderIdentityIssues);
export const patchAdminPlanPriceInputSchema = z
    .strictObject({
    expectedUpdatedAt: timestampSchema,
    planId: z.string().min(1).max(100).optional(),
    countryCode: countryCodeSchema.optional(),
    currency: currencyCodeSchema.optional(),
    termMonths: z.union([z.literal(1), z.literal(6), z.literal(12)]).optional(),
    amount: amountSchema.optional(),
    discountPercent: z.number().int().min(0).max(100).optional(),
    rail: billingRailSchema.optional(),
    externalProductId: externalProductIdSchema.optional(),
    externalPriceId: externalPriceIdSchema.optional(),
    reason: reasonSchema,
})
    .refine((value) => value.planId !== undefined ||
    value.countryCode !== undefined ||
    value.currency !== undefined ||
    value.termMonths !== undefined ||
    value.amount !== undefined ||
    value.discountPercent !== undefined ||
    value.rail !== undefined ||
    value.externalProductId !== undefined ||
    value.externalPriceId !== undefined, { error: "At least one editable field is required" })
    .superRefine(addPartialProviderIdentityIssues);
export const createAdminCreditPackInputSchema = z
    .strictObject({
    offerKey: slugSchema,
    slug: slugSchema,
    name: nameSchema,
    interactions: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    countryCode: countryCodeSchema,
    currency: currencyCodeSchema,
    amount: amountSchema,
    rail: billingRailSchema,
    externalProductId: externalProductIdSchema.default(null),
    externalPriceId: externalPriceIdSchema.default(null),
    expiresDays: z.number().int().min(1).max(3_650),
    sortOrder: z.number().int().min(0).max(1_000_000).default(0),
    reason: reasonSchema,
})
    .superRefine(addProviderIdentityIssues);
export const patchAdminCreditPackInputSchema = z
    .strictObject({
    expectedUpdatedAt: timestampSchema,
    offerKey: slugSchema.optional(),
    slug: slugSchema.optional(),
    name: nameSchema.optional(),
    interactions: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
    countryCode: countryCodeSchema.optional(),
    currency: currencyCodeSchema.optional(),
    amount: amountSchema.optional(),
    rail: billingRailSchema.optional(),
    externalProductId: externalProductIdSchema.optional(),
    externalPriceId: externalPriceIdSchema.optional(),
    expiresDays: z.number().int().min(1).max(3_650).optional(),
    sortOrder: z.number().int().min(0).max(1_000_000).optional(),
    reason: reasonSchema,
})
    .refine((value) => value.offerKey !== undefined ||
    value.slug !== undefined ||
    value.name !== undefined ||
    value.interactions !== undefined ||
    value.countryCode !== undefined ||
    value.currency !== undefined ||
    value.amount !== undefined ||
    value.rail !== undefined ||
    value.externalProductId !== undefined ||
    value.externalPriceId !== undefined ||
    value.expiresDays !== undefined ||
    value.sortOrder !== undefined, { error: "At least one editable field is required" })
    .superRefine(addPartialProviderIdentityIssues);
export const adminLifecycleInputSchema = z.strictObject({
    expectedUpdatedAt: timestampSchema,
    reason: reasonSchema,
});
export const adminOfferActivationInputSchema = adminLifecycleInputSchema
    .extend({
    replaceActiveId: uppercaseUlidSchema.optional(),
    replaceExpectedUpdatedAt: timestampSchema.optional(),
})
    .superRefine((value, context) => {
    if ((value.replaceActiveId === undefined) !== (value.replaceExpectedUpdatedAt === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Replacement ID and expected timestamp must be supplied together",
        });
    }
});
export const qualifyStagingCatalogInputSchema = z.strictObject({
    expectedLockId: z.string().min(1).max(100),
    reason: reasonSchema,
    confirmation: z.literal("activate-reviewed-catalog"),
});
function addProviderIdentityIssues(value, context) {
    if (value.rail.provider === "xendit" && value.externalProductId !== null) {
        context.addIssue({
            code: "custom",
            path: ["externalProductId"],
            message: "Xendit product identity must be null",
        });
    }
    if (value.rail.provider === "polar" &&
        value.externalPriceId !== null &&
        !polarUuid4Schema.safeParse(value.externalPriceId).success) {
        context.addIssue({
            code: "custom",
            path: ["externalPriceId"],
            message: "Polar price identity must be a canonical UUID4",
        });
    }
}
function addPartialProviderIdentityIssues(value, context) {
    if (value.rail === undefined)
        return;
    addProviderIdentityIssues({
        rail: value.rail,
        externalProductId: value.externalProductId ?? null,
        externalPriceId: value.externalPriceId ?? null,
    }, context);
}
//# sourceMappingURL=catalog.js.map